import { useState, useEffect, useRef, useCallback } from 'react';
import { AwsAccount, InvestigationScope } from '../../../types';
import { useAwsAccountService } from '../../aws-account/services/awsAccountService';

interface UseAccountSelectionResult {
  accounts: AwsAccount[];
  selectedAccountIds: string[];
  loading: boolean;
  error: string | null;
  retry: () => void;
  toggleAccount: (accountId: string, selected: boolean) => void;
  toggleAll: () => void;
  isAccountSelected: (accountId: string) => boolean;
  allSelected: boolean;
  buildScope: () => InvestigationScope[];
}

export function useAccountSelection(
  customerId: string,
  isOpen: boolean,
  initialScope: InvestigationScope[] = [],
): UseAccountSelectionResult {
  const awsAccountService = useAwsAccountService();
  const [accounts, setAccounts] = useState<AwsAccount[]>([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const accountsData = await awsAccountService.getCustomerAwsAccounts(customerId);
      const enabledAccounts = accountsData.customer_accounts.filter((account: AwsAccount) => account.enabled);
      setAccounts(enabledAccounts);
    } catch (err) {
      console.error('Failed to load AWS accounts:', err);
      setError(`Failed to load AWS accounts: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [awsAccountService, customerId]);

  useEffect(() => {
    if (isOpen && customerId && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadAccounts();
    } else if (!isOpen) {
      hasLoadedRef.current = false;
    }
  }, [isOpen, customerId, loadAccounts]);

  useEffect(() => {
    if (accounts.length > 0 && initialScope.length > 0) {
      const existingScope = initialScope[0];
      const existingAccountIds = existingScope?.accounts?.map(scopeAccount => {
        const matchingAccount = accounts.find(acc => acc.aws_account_id === scopeAccount.aws_account_id);
        return matchingAccount?.id;
      }).filter((id): id is string => id !== undefined) || [];

      setSelectedAccountIds(existingAccountIds);
    }
  }, [accounts, initialScope]);

  const toggleAccount = useCallback((accountId: string, selected: boolean) => {
    setSelectedAccountIds(prev => {
      if (selected) {
        return [...prev, accountId];
      } else {
        return prev.filter(id => id !== accountId);
      }
    });
  }, []);

  const isAccountSelected = useCallback((accountId: string): boolean => {
    return selectedAccountIds.includes(accountId);
  }, [selectedAccountIds]);

  const allSelected = accounts.length > 0 && accounts.every(account => selectedAccountIds.includes(account.id));

  const toggleAll = useCallback(() => {
    if (allSelected) {
      setSelectedAccountIds([]);
    } else {
      setSelectedAccountIds(accounts.map(account => account.id));
    }
  }, [allSelected, accounts]);

  const buildScope = useCallback((): InvestigationScope[] => {
    const scopeAccounts = selectedAccountIds
      .map(accountId => {
        const account = accounts.find(acc => acc.id === accountId);
        if (!account) return null;

        return {
          aws_account_id: account.aws_account_id,
          account_name: account.account_name,
          main_region: account.main_region,
          supported_regions: account.supported_regions
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    return [{ accounts: scopeAccounts }];
  }, [selectedAccountIds, accounts]);

  return {
    accounts,
    selectedAccountIds,
    loading,
    error,
    retry: loadAccounts,
    toggleAccount,
    toggleAll,
    isAccountSelected,
    allSelected,
    buildScope,
  };
}
